/**
 * DisplayMirrorPanel
 *
 * 1:1 live mirror of the connected display (digitale Tafel).
 * All coordinates are stored and synced in **Display-space** (the actual
 * display resolution). The mirror applies a uniform CSS scale so that the
 * full display viewport fits into the mirror container, preserving proportions
 * for both media items and pen strokes.
 */
import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Monitor, Loader2, Maximize2, Minimize2, ImagePlus, ChevronUp, ChevronDown, Shapes, Palette, Triangle, Calculator, FolderOpen, Youtube, FileText } from 'lucide-react';
import {
  fetchDisplayStatus,
  teacherDismissDisplayItem,
  teacherUpdateDisplayLayout,
  teacherUpdateDisplayTextItem,
  teacherUpdateDisplayOverlays,
  teacherReplaceDisplayDrawingStrokes,
  teacherSetActivePage,
  teacherAddPage,
  fetchYouTubeTitle,
  presentDisplayText,
  type DisplayStatus,
} from '../lib/displayApi';
import { presentQuickstartDisplayMedia, presentQuickstartDisplayImage, uploadTeachingMediaFile } from '../lib/unterrichtenApi';
import { api } from '../lib/api';
import { GeodreieckOverlay, DEFAULT_GEODREIECK, type GeodreieckState } from './GeodreieckOverlay';
import { CalculatorOverlay, DEFAULT_CALCULATOR, type CalculatorState } from './CalculatorOverlay';
import { ZirkelOverlay, DEFAULT_ZIRKEL, arcPoints, LEG_LENGTH_MIN, LEG_LENGTH_MAX, type ZirkelState } from './ZirkelOverlay';

const DRAW_COLORS = ['#FFFFFF', '#EF4444', '#FACC15', '#3B82F6', '#22C55E', '#000000'];
const DRAW_TOOLS = ['pen', 'marker', 'line', 'line-dashed', 'line-ruler', 'eraser'] as const;
type DrawTool = (typeof DRAW_TOOLS)[number];
const LINE_TWO_POINT_TOOLS = ['line', 'line-dashed', 'line-ruler'] as const;
const SHAPE_TOOLS = ['rectangle', 'square', 'circle', 'oval', 'triangle'] as const;
type ShapeTool = (typeof SHAPE_TOOLS)[number];
const FREEHAND_TOOLS = ['pen', 'marker', 'eraser'] as const;
function isFreehandTool(tool: string): boolean {
  return (FREEHAND_TOOLS as readonly string[]).includes(tool);
}

const BACKGROUND_PRESETS = [
  { label: 'Weiß', value: '#ffffff' },
  { label: 'Hellgrau', value: '#f1f5f9' },
  { label: 'Dunkel', value: '#1e293b' },
  { label: 'Schwarz', value: '#0f172a' },
] as const;

function createStrokeId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `stroke_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

const GRID_SNAP_SPACING = 40;
const GRID_SNAP_MAX_DIST = 7;
function snapToGrid(x: number, y: number): { x: number; y: number } {
  const gx = Math.round(x / GRID_SNAP_SPACING) * GRID_SNAP_SPACING;
  const gy = Math.round(y / GRID_SNAP_SPACING) * GRID_SNAP_SPACING;
  if (Math.hypot(x - gx, y - gy) <= GRID_SNAP_MAX_DIST) return { x: gx, y: gy };
  return { x, y };
}

function buildYouTubeEmbedUrl(rawUrl: string): string {
  const value = String(rawUrl || '').trim();
  if (!value) return '';
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();
    let id = '';
    if (host.includes('youtu.be')) id = parsed.pathname.replace('/', '').trim();
    else if (host.includes('youtube.com')) {
      if (parsed.pathname.startsWith('/embed/')) return value;
      if (parsed.pathname.startsWith('/shorts/')) id = parsed.pathname.replace('/shorts/', '').split('/')[0];
      else if (parsed.pathname.startsWith('/live/')) id = parsed.pathname.replace('/live/', '').split('/')[0];
      else id = parsed.searchParams.get('v') || '';
    }
    return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0` : value;
  } catch { return value; }
}

function buildPdfDisplayUrl(rawUrl: string): string {
  const base = String(rawUrl || '').trim();
  if (!base) return '';
  const flags = 'toolbar=0&navpanes=0&scrollbar=0';
  return base.includes('#') ? (base.includes('toolbar=') ? base : `${base}&${flags}`) : `${base}#${flags}`;
}

interface Props {
  displayPin: string | null;
}

export default function DisplayMirrorPanel({ displayPin }: Props) {
  const [status, setStatus] = useState<DisplayStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Layout state – always in DISPLAY coordinates
  const [mediaLayout, setMediaLayout] = useState<
    Record<string, { x: number; y: number; widthPx: number; z: number; rotationDeg: number; visible: boolean }>
  >({});
  const [locallyDismissedIds, setLocallyDismissedIds] = useState<string[]>([]);
  const [activeHandlesId, setActiveHandlesId] = useState<string | null>(null);
  const [handlesFading, setHandlesFading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const lastDragEndRef = useRef(0);
  const itemSizeRef = useRef<Record<string, { width: number; height: number }>>({});

  // Drawing state – strokes always in DISPLAY coordinates
  const [activeDrawTool, setActiveDrawTool] = useState<DrawTool | ShapeTool | null>(null);
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const [shapeMenuPosition, setShapeMenuPosition] = useState<{ left: number; bottom: number } | null>(null);
  const shapeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [activeDrawColor, setActiveDrawColor] = useState('#FFFFFF');
  const [drawWidth, setDrawWidth] = useState(4);
  const [backgroundColor, setBackgroundColor] = useState('#1e293b');
  const [showWritingLines, setShowWritingLines] = useState(false);
  const [showMathGrid, setShowMathGrid] = useState(false);
  const [showBackgroundMenu, setShowBackgroundMenu] = useState(false);
  const [backgroundMenuPosition, setBackgroundMenuPosition] = useState<{ left: number; bottom: number } | null>(null);
  const backgroundButtonRef = useRef<HTMLButtonElement | null>(null);
  const [geodreieck, setGeodreieck] = useState<GeodreieckState>(() => ({ ...DEFAULT_GEODREIECK }));
  const [calculator, setCalculator] = useState<CalculatorState>(() => ({ ...DEFAULT_CALCULATOR }));
  const [zirkel, setZirkel] = useState<ZirkelState>(() => ({ ...DEFAULT_ZIRKEL }));
  const overlayLocalUpdateAtRef = useRef(0);
  const [localDrawStrokes, setLocalDrawStrokes] = useState<Array<{
    id: string; tool: string; color: string; width: number; alpha: number;
    points: Array<{ x: number; y: number }>; createdAt: string;
  }>>([]);
  const [redoStrokes, setRedoStrokes] = useState<typeof localDrawStrokes>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [zirkelArcPreview, setZirkelArcPreview] = useState<{
    cx: number; cy: number; radius: number; startDeg: number; endDeg: number;
  } | null>(null);

  // Mirror container size (CSS pixels)
  const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({ w: 800, h: 450 });

  // ── Page navigation ───────────────────────────────────────────────────────
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isPageSwitching, setIsPageSwitching] = useState(false);

  // When display is waiting for coupling: pin can be moved into status bar so user can draw on whiteboard
  const [waitingPinInStatusBar, setWaitingPinInStatusBar] = useState(false);

  // ── Media upload / present ────────────────────────────────────────────────
  const [showMediaBar, setShowMediaBar] = useState(false);
  const [mediaUrlInput, setMediaUrlInput] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const mediaFileInputRef = useRef<HTMLInputElement | null>(null);

  // ── Dateien / YouTube / Text (Toolbar-Buttons) ─────────────────────────────
  const [showDateienModal, setShowDateienModal] = useState(false);
  const [dateienFolderStack, setDateienFolderStack] = useState<Array<{ id: number; name: string }>>([]);
  const [dateienContents, setDateienContents] = useState<{ folders: Array<{ id: number; name: string }>; files: Array<{ file_id: number; original_name: string; mime_type?: string }> }>({ folders: [], files: [] });
  const [dateienLoading, setDateienLoading] = useState(false);
  const [dateienError, setDateienError] = useState<string | null>(null);
  const [textBoxDrawMode, setTextBoxDrawMode] = useState(false);
  const [textBoxDraft, setTextBoxDraft] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [pendingFocusTextItemId, setPendingFocusTextItemId] = useState<string | null>(null);
  const textBoxStartRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);

  const drawingCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const zirkelPreviewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const drawingStrokesRef = useRef(localDrawStrokes);
  const drawingCurrentRef = useRef<(typeof localDrawStrokes)[0] | null>(null);
  const drawingDirtyRef = useRef(false);
  const drawingSyncTimerRef = useRef<number | null>(null);
  const mediaLayoutRef = useRef(mediaLayout);
  const layoutSyncTimerRef = useRef<number | null>(null);
  const fadeTimerRef = useRef<number | null>(null);
  const dragStateRef = useRef<{
    itemId: string; mode: 'move' | 'resize';
    startX: number; startY: number;
    startOffsetX: number; startOffsetY: number; startWidthPx: number; startRotationDeg: number;
    startHandleX: number; startHandleY: number;
    startDiagLength: number; startDiagAngle: number;
  } | null>(null);
  const [editingTextItemId, setEditingTextItemId] = useState<string | null>(null);
  const textItemRefs = useRef<Record<string, { titleEl: HTMLElement | null; bodyEl: HTMLElement | null }>>({});
  const textSyncTimersRef = useRef<Record<string, number | null>>({});

  useEffect(() => { drawingStrokesRef.current = localDrawStrokes; }, [localDrawStrokes]);
  useEffect(() => { mediaLayoutRef.current = mediaLayout; }, [mediaLayout]);

  const scheduleTextItemUpdate = useCallback((itemId: string, patch: { text?: string; title?: string }) => {
    if (!displayPin) return;
    const key = String(itemId);
    const prev = textSyncTimersRef.current[key];
    if (prev) window.clearTimeout(prev);
    textSyncTimersRef.current[key] = window.setTimeout(() => {
      textSyncTimersRef.current[key] = null;
      teacherUpdateDisplayTextItem({ pin: displayPin, itemId: key, ...patch }).catch(() => {});
    }, 350);
  }, [displayPin]);

  const flushTextItemUpdate = useCallback((itemId: string, patch: { text?: string; title?: string }) => {
    if (!displayPin) return;
    const key = String(itemId);
    const prev = textSyncTimersRef.current[key];
    if (prev) {
      window.clearTimeout(prev);
      textSyncTimersRef.current[key] = null;
    }
    teacherUpdateDisplayTextItem({ pin: displayPin, itemId: key, ...patch }).catch(() => {});
  }, [displayPin]);

  useEffect(() => {
    return () => {
      Object.values(textSyncTimersRef.current).forEach((timerId) => {
        if (timerId) window.clearTimeout(timerId);
      });
      textSyncTimersRef.current = {};
    };
  }, []);

  // Fixed logical canvas size – matches Display.tsx CANVAS_W/CANVAS_H.
  const CANVAS_W = 1920;
  const CANVAS_H = 1080;

  // ── Scale factor: how much to shrink Display→Mirror ───────────────────────
  const scale = useMemo(() => {
    return Math.min(containerSize.w / CANVAS_W, containerSize.h / CANVAS_H);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerSize]);

  const scaleRef = useRef(scale);
  useEffect(() => { scaleRef.current = scale; }, [scale]);

  // ── Track container size via ResizeObserver ───────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      // In CSS-overlay fullscreen the element covers the whole viewport.
      const rect = el.getBoundingClientRect();
      setContainerSize({ w: rect.width || window.innerWidth, h: rect.height || window.innerHeight });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Poll display status every 2s ──────────────────────────────────────────
  useEffect(() => {
    if (!displayPin) { setStatus(null); return; }
    let mounted = true;
    const poll = async () => {
      try {
        const next = await fetchDisplayStatus(displayPin);
        if (mounted) setStatus(next);
      } catch { /* best-effort */ }
    };
    setIsLoading(true);
    poll().finally(() => { if (mounted) setIsLoading(false); });
    const interval = window.setInterval(poll, 2000);
    return () => { mounted = false; window.clearInterval(interval); };
  }, [displayPin]);

  // When display connects, reset pin to big view for next time it waits
  useEffect(() => {
    if (status?.status === 'connected') setWaitingPinInStatusBar(false);
  }, [status?.status]);

  function mimeToDisplayMediaType(mime: string | undefined): 'image' | 'video' | 'pdf' {
    if (!mime) return 'image';
    const lower = mime.toLowerCase();
    if (lower.startsWith('video/')) return 'video';
    if (lower === 'application/pdf' || lower === 'application/x-pdf') return 'pdf';
    return 'image';
  }
  const DISPLAY_SUPPORTED_MIMES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'video/mp4', 'video/quicktime', 'video/avi',
    'application/pdf', 'application/x-pdf'
  ];
  function isDisplaySupportedFile(mime: string | undefined): boolean {
    if (!mime) return false;
    const lower = mime.toLowerCase();
    if (lower.startsWith('image/') || lower.startsWith('video/') || lower === 'application/pdf' || lower === 'application/x-pdf') return true;
    return DISPLAY_SUPPORTED_MIMES.includes(lower);
  }

  // ── Load Dateien when modal opens (teacher auth) ────────────────────────────
  useEffect(() => {
    if (!showDateienModal || !displayPin) return;
    setDateienError(null);
    setDateienLoading(true);
    const load = async () => {
      try {
        if (dateienFolderStack.length === 0) {
          const res = await api.get<{ folders: Array<{ id: number; name: string }>; files: Array<{ file_id: number; original_name: string; mime_type?: string }> }>('/dateien/root');
          const data = res.data;
          setDateienContents({
            folders: Array.isArray(data.folders) ? data.folders : [],
            files: Array.isArray(data.files) ? data.files : []
          });
        } else {
          const top = dateienFolderStack[dateienFolderStack.length - 1];
          const res = await api.get<{ subfolders: Array<{ id: number; name: string }>; files: Array<{ file_id: number; original_name: string; mime_type?: string }> }>(`/dateien/folders/${top.id}`);
          const data = res.data;
          setDateienContents({
            folders: Array.isArray(data.subfolders) ? data.subfolders : [],
            files: Array.isArray(data.files) ? data.files : []
          });
        }
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Dateien konnten nicht geladen werden.';
        setDateienError(msg);
        setDateienContents({ folders: [], files: [] });
      } finally {
        setDateienLoading(false);
      }
    };
    load();
  }, [showDateienModal, dateienFolderStack, displayPin]);

  const openDateienModal = () => {
    setDateienFolderStack([]);
    setDateienError(null);
    setShowDateienModal(true);
  };
  const navigateDateienFolder = (folder: { id: number; name: string }) => {
    setDateienFolderStack((prev) => [...prev, folder]);
  };
  const navigateDateienBack = () => {
    setDateienFolderStack((prev) => (prev.length <= 1 ? [] : prev.slice(0, -1)));
  };
  const presentDateienFile = async (file: { file_id: number; original_name: string; mime_type?: string }) => {
    if (!displayPin) return;
    try {
      // Do not send protected /dateien URLs directly to the display device.
      // Fetch authenticated blob in teacher session, upload to display_media, then present.
      const resp = await api.get<Blob>(`/dateien/files/${file.file_id}/view`, { responseType: 'blob' as any });
      const blob = resp.data as unknown as Blob;
      const mime = String(file.mime_type || blob.type || '');
      const safeName = String(file.original_name || 'display-file').trim() || 'display-file';
      const uploadFile = new File([blob], safeName, { type: mime || undefined });
      const uploaded = await uploadTeachingMediaFile(uploadFile, safeName);
      const mediaUrl = String(uploaded?.mediaUrl || '');
      if (!mediaUrl) throw new Error('Upload lieferte keine URL');
      const mediaType = mimeToDisplayMediaType(String(uploaded?.mimeType || mime));
      if (mediaType === 'image') {
        await presentQuickstartDisplayImage({ pin: displayPin, imageUrl: mediaUrl, title: safeName });
      } else {
        await presentQuickstartDisplayMedia({
          pin: displayPin,
          assetId: uploaded?.assetId ? Number(uploaded.assetId) : null,
          mediaUrl,
          mediaType,
          mimeType: String(uploaded?.mimeType || mime || ''),
          title: safeName,
          sourcePageUrl: mediaUrl
        });
      }
      setShowDateienModal(false);
    } catch (err: any) {
      setDateienError(err?.response?.data?.error || err?.message || 'Datei konnte nicht auf dem Display angezeigt werden.');
    }
  };
  const pointerToCanvas = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * CANVAS_W;
    const y = ((event.clientY - rect.top) / rect.height) * CANVAS_H;
    return {
      x: Math.max(0, Math.min(CANVAS_W, x)),
      y: Math.max(0, Math.min(CANVAS_H, y)),
    };
  };


  const createTextItemFromDraft = useCallback(async (draft: { x: number; y: number; width: number; height: number }) => {
    if (!displayPin) return;
    try {
      const data = await presentDisplayText({ pin: displayPin, text: 'Text eingeben…' }) as { itemId?: string } | undefined;
      if (!data?.itemId) return;
      const maxZ = Math.max(0, ...Object.values(mediaLayoutRef.current).map((l) => Number(l?.z || 0)));
      const widthPx = Math.max(180, Math.round(draft.width));
      const x = Math.round(draft.x + draft.width / 2 - CANVAS_W / 2);
      const y = Math.round(draft.y + draft.height / 2 - CANVAS_H / 2);
      await teacherUpdateDisplayLayout({
        pin: displayPin,
        layouts: [{ itemId: data.itemId, x, y, widthPx, z: maxZ + 1, rotationDeg: 0 }]
      });
      setPendingFocusTextItemId(data.itemId);
    } catch {
      // best-effort: silently ignore transient display-sync errors
    }
  }, [displayPin]);

  // ── Derive media items from status (incl. legacy fallback) ────────────────
  const mediaItems = useMemo(() => {
    if (status?.status !== 'connected') return [];

    const list = Array.isArray(status?.connectedContext?.contentItems) ? status.connectedContext!.contentItems! : [];
    const fromList = list
      .map((item) => {
        if (item?.type === 'text') {
          return {
            id: String(item?.id || ''),
            type: 'text' as const,
            text: String(item?.text || ''),
            title: String(item?.title || '')
          };
        }
        if (item?.type === 'media') {
          const mt = item?.mediaType === 'video' || item?.mediaType === 'pdf' || item?.mediaType === 'youtube' ? item.mediaType : 'image';
          return {
            id: String(item?.id || ''),
            type: 'media' as const,
            mediaUrl: String(item?.mediaUrl || ''),
            mediaType: mt as 'image' | 'video' | 'pdf' | 'youtube',
            mimeType: String(item?.mimeType || ''),
            title: String(item?.title || '')
          };
        }
        return {
          id: String(item?.id || ''),
          type: 'image' as const,
          imageUrl: String(item?.imageUrl || ''),
          title: String(item?.title || '')
        };
      })
      .filter((item) =>
        item.id &&
        (item.type === 'text'
          ? Boolean((item as any).text)
          : item.type === 'media'
          ? Boolean((item as any).mediaUrl)
          : Boolean((item as any).imageUrl))
      );

    if (fromList.length > 0) return fromList;

    // Legacy single-item fallback must only run in non-paged contexts.
    if (status?.connectedContext?.hasPages) return [];

    const legacy = status?.connectedContext?.content;
    if (legacy?.type === 'text' && legacy.text) {
      return [
        {
          id: `legacy-text:${status?.connectedContext?.contentUpdatedAt || ''}`,
          type: 'text' as const,
          text: String(legacy.text),
          title: String(legacy.title || '')
        }
      ];
    }
    if (legacy?.type === 'image' && legacy.imageUrl) {
      return [
        {
          id: `legacy:${legacy.imageUrl}:${status?.connectedContext?.contentUpdatedAt || ''}`,
          type: 'image' as const,
          imageUrl: String(legacy.imageUrl),
          title: String(legacy.title || '')
        }
      ];
    }

    return [];
  }, [status?.status, status?.connectedContext?.contentItems, status?.connectedContext?.content, status?.connectedContext?.contentUpdatedAt]);

  const visibleItems = useMemo(() => mediaItems.filter((i) => !locallyDismissedIds.includes(i.id)), [mediaItems, locallyDismissedIds]);


  // Sync server text into contentEditable when not editing (WYSIWYG text items)
  useLayoutEffect(() => {
    mediaItems
      .filter((i) => i.type === 'text')
      .forEach((item) => {
        if (editingTextItemId === item.id) return;
        const refs = textItemRefs.current[item.id];
        if (!refs) return;
        if (refs.bodyEl) refs.bodyEl.innerText = (item as { text?: string }).text ?? '';
        if (refs.titleEl) refs.titleEl.innerText = (item as { title?: string }).title ?? '';
      });
  }, [mediaItems, editingTextItemId]);

  useEffect(() => {
    if (!pendingFocusTextItemId) return;
    const refs = textItemRefs.current[pendingFocusTextItemId];
    if (!refs?.bodyEl) return;
    refs.bodyEl.focus();
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(refs.bodyEl);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    setPendingFocusTextItemId(null);
  }, [mediaItems, pendingFocusTextItemId]);

  // ── Sync layout from server (Display coords) ─────────────────────────────
  useEffect(() => {
    setLocallyDismissedIds((prev) => prev.filter((id) => mediaItems.find((it) => it.id === id)));
    // Skip layout sync while dragging or in grace period after drag
    if (isDragging || Date.now() - lastDragEndRef.current < 1500) return;
    const serverItems = Array.isArray(status?.connectedContext?.contentItems) ? status!.connectedContext!.contentItems! : [];
    const serverLayoutMap: Record<string, { x: number; y: number; widthPx: number; z: number; rotationDeg: number }> = {};
    for (const si of serverItems) {
      if (si?.id && Number(si.layoutWidth) > 0) {
        serverLayoutMap[String(si.id)] = {
          x: Number(si.layoutX) || 0, y: Number(si.layoutY) || 0,
          widthPx: Number(si.layoutWidth) || 520,
          z: Math.round(Number(si.layoutZ) || 0),
          rotationDeg: Number(si.layoutRotation) || 0,
        };
      }
    }
    setMediaLayout((prev) => {
      const next: typeof prev = {};
      mediaItems.forEach((item, index) => {
        const server = serverLayoutMap[item.id];
        if (server && server.widthPx > 0) { next[item.id] = { ...server, visible: true }; return; }
        const existing = prev[item.id];
        if (existing) { next[item.id] = existing; return; }
        const defaultWidth = Math.max(260, Math.min(CANVAS_W * 0.4, 760));
        next[item.id] = { x: index * 26, y: index * 26, widthPx: defaultWidth, z: index + 1, rotationDeg: 0, visible: true };
      });
      mediaLayoutRef.current = next;
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.connectedContext?.contentItems, isDragging]);

  // ── Sync drawing strokes from server (Display coords) ─────────────────────
  const serverStrokes = useMemo(() => {
    const raw = status?.connectedContext?.drawing?.strokes;
    if (!Array.isArray(raw)) return [];
    return raw.map((s) => ({
      id: String(s?.id || ''), tool: String(s?.tool || 'pen'), color: String(s?.color || '#FFFFFF'),
      width: Number(s?.width || 2), alpha: Number(s?.alpha || 1),
      points: Array.isArray(s?.points) ? s.points.map((p: any) => ({ x: Number(p?.x||0), y: Number(p?.y||0) })) : [],
      createdAt: String(s?.createdAt || '')
    })).filter((s) => s.id && s.points.length >= 1);
  }, [status?.connectedContext?.drawing?.strokes]);

  useEffect(() => {
    if (isDrawing || drawingDirtyRef.current) return;
    setLocalDrawStrokes(serverStrokes);
  }, [serverStrokes, isDrawing]);

  // ── Sync page index from server status ────────────────────────────────────
  useEffect(() => {
    const serverIdx = status?.activePageIndex ?? 0;
    const serverTotal = status?.totalPages ?? 1;
    setTotalPages(serverTotal);
    if (serverIdx !== activePageIndex) {
      // Page changed → clear local drawing/layout so server data takes over
      setActivePageIndex(serverIdx);
      setLocalDrawStrokes([]);
      setMediaLayout({});
      drawingStrokesRef.current = [];
      drawingDirtyRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.activePageIndex, status?.totalPages]);

  // ── Sync overlay states from server status ────────────────────────────────
  useEffect(() => {
    // Avoid snapping back while user is actively moving/toggling overlays locally.
    if (Date.now() - overlayLocalUpdateAtRef.current < 1200) return;
    const g = status?.connectedContext?.geodreieck;
    const c = status?.connectedContext?.calculator;
    const z = status?.connectedContext?.zirkel;
    if (g && typeof g === 'object') {
      setGeodreieck((prev) => ({
        ...DEFAULT_GEODREIECK,
        ...prev,
        visible: Boolean(g.visible),
        x: Number(g.x) ?? prev.x,
        y: Number(g.y) ?? prev.y,
        rotation: Number(g.rotation) ?? prev.rotation,
        scale: Math.max(0.4, Math.min(6, Number(g.scale) || 1)),
      }));
    }
    if (c && typeof c === 'object') {
      setCalculator((prev) => ({
        ...DEFAULT_CALCULATOR,
        ...prev,
        visible: Boolean(c.visible),
        x: Number(c.x) ?? prev.x,
        y: Number(c.y) ?? prev.y,
      }));
    }
    if (z && typeof z === 'object') {
      setZirkel((prev) => ({
        ...DEFAULT_ZIRKEL,
        ...prev,
        visible: Boolean(z.visible),
        x: Number(z.x) ?? prev.x,
        y: Number(z.y) ?? prev.y,
        radius: Math.max(20, Math.min(500, Number(z.radius) || 120)),
        legLength: Math.max(LEG_LENGTH_MIN, Math.min(LEG_LENGTH_MAX, Number(z.legLength) || 90)),
      }));
    }
  }, [status?.connectedContext?.geodreieck, status?.connectedContext?.calculator, status?.connectedContext?.zirkel]);

  // ── Push overlay state to server (debounced) ───────────────────────────────
  const overlayPushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!displayPin || status?.status !== 'connected') return;
    if (overlayPushTimeoutRef.current) clearTimeout(overlayPushTimeoutRef.current);
    overlayPushTimeoutRef.current = setTimeout(() => {
      overlayPushTimeoutRef.current = null;
      teacherUpdateDisplayOverlays({
        pin: displayPin,
        geodreieck: { visible: geodreieck.visible, x: geodreieck.x, y: geodreieck.y, rotation: geodreieck.rotation, scale: geodreieck.scale },
        calculator: { visible: calculator.visible, x: calculator.x, y: calculator.y },
        zirkel: { visible: zirkel.visible, x: zirkel.x, y: zirkel.y, radius: zirkel.radius, legLength: zirkel.legLength ?? DEFAULT_ZIRKEL.legLength },
      }).catch(() => { /* best-effort */ });
    }, 400);
    return () => {
      if (overlayPushTimeoutRef.current) clearTimeout(overlayPushTimeoutRef.current);
    };
  }, [displayPin, status?.status, geodreieck.visible, geodreieck.x, geodreieck.y, geodreieck.rotation, geodreieck.scale, calculator.visible, calculator.x, calculator.y, zirkel.visible, zirkel.x, zirkel.y, zirkel.radius, zirkel.legLength]);

  const showCanvas = status?.status === 'connected' || (status?.status === 'waiting' && waitingPinInStatusBar);
  useEffect(() => {
    if (status?.status === 'waiting' && waitingPinInStatusBar) setActiveDrawTool('pen');
  }, [status?.status, waitingPinInStatusBar]);

  useLayoutEffect(() => {
    if (!showShapeMenu || !shapeButtonRef.current) {
      setShapeMenuPosition(null);
      return;
    }
    const rect = shapeButtonRef.current.getBoundingClientRect();
    setShapeMenuPosition({ left: rect.left, bottom: window.innerHeight - rect.top + 4 });
  }, [showShapeMenu]);

  useLayoutEffect(() => {
    if (!showBackgroundMenu || !backgroundButtonRef.current) {
      setBackgroundMenuPosition(null);
      return;
    }
    const rect = backgroundButtonRef.current.getBoundingClientRect();
    setBackgroundMenuPosition({ left: rect.left, bottom: window.innerHeight - rect.top + 4 });
  }, [showBackgroundMenu]);

  const drawBackgroundAndGuides = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const dpr = window.devicePixelRatio || 1;
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      const lineColor = luminance < 0.5 ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)';
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 1;
      if (showWritingLines) {
        const lineSpacing = 48;
        for (let y = 0; y <= CANVAS_H; y += lineSpacing) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(CANVAS_W, y);
          ctx.stroke();
        }
      }
      if (showMathGrid) {
        const gridSpacing = 40;
        for (let x = 0; x <= CANVAS_W; x += gridSpacing) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, CANVAS_H);
          ctx.stroke();
        }
        for (let y = 0; y <= CANVAS_H; y += gridSpacing) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(CANVAS_W, y);
          ctx.stroke();
        }
      }
      ctx.restore();
    },
    [backgroundColor, showWritingLines, showMathGrid]
  );

  // ── Canvas: always sized to 1920×1080, CSS-scaled to fit; re-run when canvas is shown or zirkel opened ────────────────
  useEffect(() => {
    if (!showCanvas) return;
    const dpr = window.devicePixelRatio || 1;
    const canvases = [drawingCanvasRef.current];
    if (zirkel.visible) canvases.push(zirkelPreviewCanvasRef.current);
    for (const canvas of canvases) {
      if (!canvas) continue;
      canvas.width = Math.floor(CANVAS_W * dpr);
      canvas.height = Math.floor(CANVAS_H * dpr);
      canvas.style.width = `${CANVAS_W}px`;
      canvas.style.height = `${CANVAS_H}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  }, [showCanvas, zirkel.visible]);

  const drawShape = (
    ctx: CanvasRenderingContext2D,
    tool: string,
    pts: Array<{ x: number; y: number }>,
    color: string,
    width: number,
    alpha: number
  ) => {
    if (pts.length < 2 || !SHAPE_TOOLS.includes(tool as ShapeTool)) return;
    const x0 = pts[0].x, y0 = pts[0].y, x1 = pts[1].x, y1 = pts[1].y;
    const left = Math.min(x0, x1), top = Math.min(y0, y1);
    const w = Math.abs(x1 - x0), h = Math.abs(y1 - y0);
    if (tool === 'rectangle') {
      ctx.strokeRect(left, top, w || 1, h || 1);
    } else if (tool === 'square') {
      const s = Math.max(w, h, 1);
      const sx = (x0 + x1) / 2 - s / 2, sy = (y0 + y1) / 2 - s / 2;
      ctx.strokeRect(sx, sy, s, s);
    } else if (tool === 'circle') {
      const r = Math.hypot(x1 - x0, y1 - y0) || 1;
      ctx.beginPath();
      ctx.arc(x0, y0, r, 0, Math.PI * 2);
      ctx.stroke();
    } else if (tool === 'oval') {
      const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
      const rx = Math.max(w / 2, 1), ry = Math.max(h / 2, 1);
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (tool === 'triangle') {
      const mx = (x0 + x1) / 2, baseLen = Math.hypot(x1 - x0, y1 - y0) || 1;
      const peakY = Math.min(y0, y1) - baseLen;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.lineTo(mx, peakY);
      ctx.closePath();
      ctx.stroke();
    }
  };

  // ── Canvas redraw (1920×1080 coords, no conversion needed) ──────────────
  useEffect(() => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    drawBackgroundAndGuides(ctx);
    localDrawStrokes.forEach((stroke) => {
      if (!stroke.points.length) return;
      const isEraser = stroke.tool === 'eraser';
      ctx.save();
      ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
      ctx.strokeStyle = isEraser ? 'rgba(0,0,0,1)' : stroke.color;
      ctx.globalAlpha = Math.max(0, Math.min(1, stroke.alpha));
      ctx.lineWidth = stroke.width || 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (stroke.points.length >= 2 && SHAPE_TOOLS.includes(stroke.tool as ShapeTool)) {
        drawShape(ctx, stroke.tool, stroke.points, stroke.color, stroke.width, stroke.alpha);
      } else if ((stroke.tool === 'line' || stroke.tool === 'line-dashed' || stroke.tool === 'line-ruler') && stroke.points.length >= 2) {
        const isDashed = stroke.tool === 'line-dashed';
        const isRuler = stroke.tool === 'line-ruler';
        if (isDashed) ctx.setLineDash([8, 6]);
        ctx.beginPath();
        ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
        if (stroke.points.length === 2) {
          ctx.lineTo(stroke.points[1].x, stroke.points[1].y);
        } else {
          for (let i = 1; i < stroke.points.length; i++) ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }
        ctx.stroke();
        if (isDashed) ctx.setLineDash([]);
        if (isRuler) {
          const pts = stroke.points;
          const x0 = pts[0].x, y0 = pts[0].y, x1 = pts[pts.length - 1].x, y1 = pts[pts.length - 1].y;
          const dx = x1 - x0, dy = y1 - y0;
          const len = Math.hypot(dx, dy) || 1;
          const perpX = -dy / len, perpY = dx / len;
          const tickSpacing = 40, tickLen = 8;
          for (let d = 0; d <= len; d += tickSpacing) {
            const t = d / len, cx = x0 + t * dx, cy = y0 + t * dy;
            ctx.beginPath();
            ctx.moveTo(cx - perpX * tickLen, cy - perpY * tickLen);
            ctx.lineTo(cx + perpX * tickLen, cy + perpY * tickLen);
            ctx.stroke();
          }
        }
      } else {
        ctx.beginPath();
        stroke.points.forEach((pt, i) => (i === 0 ? ctx.moveTo(pt.x, pt.y) : ctx.lineTo(pt.x, pt.y)));
        ctx.stroke();
      }
      ctx.restore();
    });
  }, [localDrawStrokes, drawBackgroundAndGuides]);

  // Zirkel: Live-Vorschau des Kreisbogens während des Drehens am Griff
  useEffect(() => {
    const canvas = zirkelPreviewCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    if (zirkelArcPreview) {
      const points = arcPoints(
        zirkelArcPreview.cx, zirkelArcPreview.cy, zirkelArcPreview.radius,
        zirkelArcPreview.startDeg, zirkelArcPreview.endDeg
      );
      if (points.length >= 2) {
        ctx.save();
        ctx.strokeStyle = activeDrawColor;
        ctx.lineWidth = drawWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
        ctx.stroke();
        ctx.restore();
      }
    }
  }, [zirkelArcPreview, activeDrawColor, drawWidth]);

  // ── Schedule drawing sync to server ───────────────────────────────────────
  const scheduleDrawingSync = useCallback(() => {
    if (!displayPin) return;
    drawingDirtyRef.current = true;
    if (drawingSyncTimerRef.current) window.clearTimeout(drawingSyncTimerRef.current);
    drawingSyncTimerRef.current = window.setTimeout(async () => {
      try {
        await teacherReplaceDisplayDrawingStrokes({ pin: displayPin, strokes: drawingStrokesRef.current });
        drawingDirtyRef.current = false;
      } catch { /* best-effort */ }
    }, 500);
  }, [displayPin]);

  // ── Schedule layout sync to server (Display coords) ───────────────────────
  const scheduleLayoutSync = useCallback(() => {
    if (!displayPin) return;
    if (layoutSyncTimerRef.current) window.clearTimeout(layoutSyncTimerRef.current);
    layoutSyncTimerRef.current = window.setTimeout(async () => {
      const layouts = Object.entries(mediaLayoutRef.current).map(([itemId, l]) => ({
        itemId, x: l.x, y: l.y, widthPx: l.widthPx, z: l.z || 0, rotationDeg: l.rotationDeg || 0
      }));
      if (layouts.length === 0) return;
      try { await teacherUpdateDisplayLayout({ pin: displayPin, layouts }); } catch { /* best-effort */ }
    }, 600);
  }, [displayPin]);


  // ── Handles ───────────────────────────────────────────────────────────────
  const activateHandles = useCallback((itemId: string) => {
    setActiveHandlesId(itemId);
    setHandlesFading(false);
    if (fadeTimerRef.current) window.clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = window.setTimeout(() => {
      setHandlesFading(true);
      fadeTimerRef.current = window.setTimeout(() => setActiveHandlesId(null), 600);
    }, 2800);
  }, []);

  const moveLayer = (itemId: string, dir: 'up' | 'down') => {
    setMediaLayout((prev) => {
      const next = { ...prev, [itemId]: { ...(prev[itemId] || { x: 0, y: 0, widthPx: 520, z: 1, rotationDeg: 0, visible: true }), z: Math.max(1, (prev[itemId]?.z || 1) + (dir === 'up' ? 1 : -1)) } };
      mediaLayoutRef.current = next;
      return next;
    });
    scheduleLayoutSync();
  };

  // ── Drag (Mirror-pixel deltas → converted to Display coords) ─────────────
  const dragTargetRef = useRef<HTMLElement | null>(null);

  const handleStartMediaDrag = (itemId: string, mode: 'move' | 'resize', event: React.PointerEvent) => {
    event.stopPropagation();
    const layout = mediaLayoutRef.current[itemId] || { x: 0, y: 0, widthPx: 520, z: 1, rotationDeg: 0, visible: true };
    const itemShell = (event.currentTarget as HTMLElement).closest('[data-media-item-shell="true"]') as HTMLElement | null;
    const contentEl = itemShell?.querySelector('[data-media-item-content="true"]') as HTMLElement | null;
    const measuredHeight = Math.max(60, Number(contentEl?.offsetHeight || Math.round(layout.widthPx * 0.56)));
    const startRotationDeg = layout.rotationDeg || 0;
    const startRotationRad = (startRotationDeg * Math.PI) / 180;
    const cos = Math.cos(startRotationRad);
    const sin = Math.sin(startRotationRad);
    const halfW = layout.widthPx / 2;
    const halfH = measuredHeight / 2;
    const diagDx = halfW * cos - halfH * sin;
    const diagDy = halfW * sin + halfH * cos;
    const startDiagLength = Math.max(1, Math.hypot(diagDx, diagDy));
    const startDiagAngle = Math.atan2(diagDy, diagDx);
    dragStateRef.current = {
      itemId,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      startOffsetX: layout.x,
      startOffsetY: layout.y,
      startWidthPx: layout.widthPx,
      startRotationDeg,
      startHandleX: layout.x + diagDx,
      startHandleY: layout.y + diagDy,
      startDiagLength,
      startDiagAngle
    };
    // Capture the pointer so all subsequent events go to this element
    const target = event.currentTarget as HTMLElement;
    try { target.setPointerCapture(event.pointerId); } catch { /* ignore */ }
    dragTargetRef.current = target;
    setIsDragging(true);
    activateHandles(itemId);
  };

  useEffect(() => {
    const onMove = (ev: PointerEvent) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      const s = scaleRef.current;
      const dx = (ev.clientX - drag.startX) / s;
      const dy = (ev.clientY - drag.startY) / s;
      if (drag.mode === 'move') {
        setMediaLayout((prev) => {
          const next = { ...prev, [drag.itemId]: { ...(prev[drag.itemId] || { z: 1, visible: true, widthPx: 520, rotationDeg: 0 }), x: drag.startOffsetX + dx, y: drag.startOffsetY + dy } };
          mediaLayoutRef.current = next;
          return next;
        });
      } else {
        const pX = drag.startHandleX + dx;
        const pY = drag.startHandleY + dy;
        const vX = pX - drag.startOffsetX;
        const vY = pY - drag.startOffsetY;
        const rawAngle = Math.atan2(vY, vX);
        let angleDelta = rawAngle - drag.startDiagAngle;
        while (angleDelta > Math.PI) angleDelta -= Math.PI * 2;
        while (angleDelta < -Math.PI) angleDelta += Math.PI * 2;
        const rawScale = Math.hypot(vX, vY) / drag.startDiagLength;
        const maxWidth = CANVAS_W * 0.96;
        const newWidth = Math.max(180, Math.min(maxWidth, drag.startWidthPx * Math.max(0.25, rawScale)));
        const nextRotation = drag.startRotationDeg + (angleDelta * 180) / Math.PI;
        setMediaLayout((prev) => {
          const next = {
            ...prev,
            [drag.itemId]: {
              ...(prev[drag.itemId] || { x: 0, y: 0, z: 1, visible: true, rotationDeg: 0 }),
              widthPx: Math.round(newWidth),
              rotationDeg: Math.round(nextRotation * 10) / 10,
              // Rotate/scale around the item's center.
              x: drag.startOffsetX,
              y: drag.startOffsetY
            }
          };
          mediaLayoutRef.current = next;
          return next;
        });
      }
    };
    const onUp = (ev: PointerEvent) => {
      if (dragStateRef.current) scheduleLayoutSync();
      // Release pointer capture
      const target = dragTargetRef.current;
      if (target) {
        try { target.releasePointerCapture(ev.pointerId); } catch { /* ignore */ }
        dragTargetRef.current = null;
      }
      dragStateRef.current = null;
      setIsDragging(false);
      lastDragEndRef.current = Date.now();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [scheduleLayoutSync]);

  // ── Dismiss ───────────────────────────────────────────────────────────────
  const handleDismiss = async (itemId: string) => {
    setLocallyDismissedIds((prev) => prev.includes(itemId) ? prev : [...prev, itemId]);
    if (!displayPin || itemId.startsWith('legacy:')) return;
    try { await teacherDismissDisplayItem({ pin: displayPin, itemId }); }
    catch { setLocallyDismissedIds((prev) => prev.filter((id) => id !== itemId)); }
  };

  // ── Drawing: pointer coords → 1920×1080 canvas coords ────────────────────
  // The canvas is 1920×1080 CSS-px, CSS-scaled by `scale`. getBoundingClientRect
  // gives the visual (scaled) rect, so dividing by scale gives canvas coords.
  const pointerToDisplay = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const s = scaleRef.current || 1;
    return {
      x: (event.clientX - rect.left) / s,
      y: (event.clientY - rect.top) / s,
    };
  };

  const beginDrawing = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!activeDrawTool) return;
    let { x, y } = pointerToDisplay(event);
    if (showMathGrid && !isFreehandTool(activeDrawTool)) {
      const sn = snapToGrid(x, y);
      x = sn.x;
      y = sn.y;
    }
    const stroke = activeDrawTool === 'marker'
      ? { id: createStrokeId(), tool: activeDrawTool, color: activeDrawColor, width: Math.max(drawWidth * 2.2, 10), alpha: 0.35, points: [{ x, y }], createdAt: new Date().toISOString() }
      : activeDrawTool === 'eraser'
      ? { id: createStrokeId(), tool: activeDrawTool, color: '#000000', width: Math.max(drawWidth * 2.5, 12), alpha: 1, points: [{ x, y }], createdAt: new Date().toISOString() }
      : (LINE_TWO_POINT_TOOLS as readonly string[]).includes(activeDrawTool) || (SHAPE_TOOLS as readonly string[]).includes(activeDrawTool)
      ? { id: createStrokeId(), tool: activeDrawTool, color: activeDrawColor, width: drawWidth, alpha: 1, points: [{ x, y }, { x, y }], createdAt: new Date().toISOString() }
      : { id: createStrokeId(), tool: activeDrawTool, color: activeDrawColor, width: drawWidth, alpha: 1, points: [{ x, y }], createdAt: new Date().toISOString() };
    drawingCurrentRef.current = stroke;
    setIsDrawing(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrawing = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = drawingCurrentRef.current;
    if (!stroke) return;
    let { x, y } = pointerToDisplay(event);
    if (showMathGrid && !isFreehandTool(stroke.tool)) {
      const sn = snapToGrid(x, y);
      x = sn.x;
      y = sn.y;
    }
    const updated = (LINE_TWO_POINT_TOOLS as readonly string[]).includes(stroke.tool) || SHAPE_TOOLS.includes(stroke.tool as ShapeTool)
      ? { ...stroke, points: [stroke.points[0], { x, y }] }
      : { ...stroke, points: [...stroke.points, { x, y }] };
    drawingCurrentRef.current = updated;
    setLocalDrawStrokes((prev) => {
      const next = [...prev.filter((s) => s.id !== updated.id), updated];
      drawingStrokesRef.current = next;
      return next;
    });
  };

  const endDrawing = () => {
    const stroke = drawingCurrentRef.current;
    if (!stroke) return;
    drawingCurrentRef.current = null;
    setIsDrawing(false);
    setRedoStrokes([]);
    setLocalDrawStrokes((prev) => {
      const next = [...prev.filter((s) => s.id !== stroke.id), stroke];
      drawingStrokesRef.current = next;
      return next;
    });
    scheduleDrawingSync();
  };

  const handleUndo = () => {
    setLocalDrawStrokes((prev) => {
      if (prev.length === 0) return prev;
      setRedoStrokes((r) => [prev[prev.length - 1], ...r]);
      const next = prev.slice(0, -1);
      drawingStrokesRef.current = next;
      scheduleDrawingSync();
      return next;
    });
  };

  const handleRedo = () => {
    setRedoStrokes((prev) => {
      if (prev.length === 0) return prev;
      setLocalDrawStrokes((s) => { const next = [...s, prev[0]]; drawingStrokesRef.current = next; scheduleDrawingSync(); return next; });
      return prev.slice(1);
    });
  };

  const handleClearDrawing = () => {
    setLocalDrawStrokes([]); setRedoStrokes([]); drawingStrokesRef.current = [];
    if (displayPin) teacherReplaceDisplayDrawingStrokes({ pin: displayPin, strokes: [] }).catch(() => {});
  };

  const handleZirkelDrawArc = useCallback(
    (cx: number, cy: number, radius: number, startDeg: number, endDeg: number) => {
      const points = arcPoints(cx, cy, radius, startDeg, endDeg);
      if (points.length < 2) return;
      const stroke = {
        id: createStrokeId(),
        tool: 'line' as const,
        color: activeDrawColor,
        width: drawWidth,
        alpha: 1,
        points,
        createdAt: new Date().toISOString(),
      };
      setLocalDrawStrokes((prev) => {
        const next = [...prev, stroke];
        drawingStrokesRef.current = next;
        scheduleDrawingSync();
        return next;
      });
      setRedoStrokes([]);
    },
    [activeDrawColor, drawWidth, scheduleDrawingSync]
  );

  const isHandMode = !activeDrawTool;

  // ── Page navigation handlers ──────────────────────────────────────────────
  const handleSetPage = async (newIndex: number) => {
    if (!displayPin || isPageSwitching || newIndex < 0 || newIndex >= totalPages) return;
    setIsPageSwitching(true);
    try {
      await teacherSetActivePage({ pin: displayPin, pageIndex: newIndex });
    } catch { /* best-effort */ } finally {
      setIsPageSwitching(false);
    }
  };

  const handleAddPage = async () => {
    if (!displayPin || isPageSwitching) return;
    setIsPageSwitching(true);
    try {
      await teacherAddPage({ pin: displayPin });
    } catch { /* best-effort */ } finally {
      setIsPageSwitching(false);
    }
  };

  // ── Fullscreen ────────────────────────────────────────────────────────────
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleToggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    // Prefer native Fullscreen API; fall back to CSS overlay (iOS Safari).
    if (!isFullscreen) {
      if (el.requestFullscreen) {
        el.requestFullscreen().catch(() => {
          setIsFullscreen(true);
          setContainerSize({ w: window.innerWidth, h: window.innerHeight });
        });
      } else if ((el as any).webkitRequestFullscreen) {
        (el as any).webkitRequestFullscreen();
      } else {
        // iOS fallback: CSS overlay
        setIsFullscreen(true);
        setContainerSize({ w: window.innerWidth, h: window.innerHeight });
      }
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => setIsFullscreen(false));
      } else if ((document as any).webkitFullscreenElement) {
        (document as any).webkitExitFullscreen();
      } else {
        setIsFullscreen(false);
        // Re-measure after leaving CSS overlay.
        setTimeout(() => {
          const rect = el.getBoundingClientRect();
          if (rect.width) setContainerSize({ w: rect.width, h: rect.height });
        }, 50);
      }
    }
  }, [isFullscreen]);

  useEffect(() => {
    const onFsChange = () => {
      const active = !!(document.fullscreenElement || (document as any).webkitFullscreenElement);
      setIsFullscreen(active);
      // Re-measure container after fullscreen transition completes.
      setTimeout(() => {
        const el = containerRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          setContainerSize({ w: rect.width || window.innerWidth, h: rect.height || window.innerHeight });
        }
      }, 100);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  // ── Media present handlers ────────────────────────────────────────────────

  const handlePresentUrl = async () => {
    const url = mediaUrlInput.trim();
    if (!url || !displayPin) return;
    const lowerUrl = url.toLowerCase();
    const isYoutube = lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be');
    const isPdf = lowerUrl.endsWith('.pdf');
    const isVideo = /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(lowerUrl);
    const mediaType: 'youtube' | 'pdf' | 'video' | 'image' = isYoutube ? 'youtube' : isPdf ? 'pdf' : isVideo ? 'video' : 'image';
    let title: string | undefined;
    if (isYoutube) {
      const result = await fetchYouTubeTitle(url);
      title = result.title ?? undefined;
    }
    try {
      await presentQuickstartDisplayMedia({ pin: displayPin, mediaUrl: url, mediaType, title, sourcePageUrl: url });
      setMediaUrlInput('');
      setShowMediaBar(false);
    } catch {
      /* ignore */
    }
  };

  const handleUploadFile = async (file: File) => {
    if (!displayPin) return;
    setIsUploading(true);
    try {
      const uploaded = await uploadTeachingMediaFile(file);
      const mediaUrl = String(uploaded?.mediaUrl || '');
      if (!mediaUrl) return;
      const mime = String(uploaded?.mimeType || file.type || '');
      const mediaType: 'video' | 'pdf' | 'image' = mime.startsWith('video/') ? 'video' : mime === 'application/pdf' ? 'pdf' : 'image';
      await presentQuickstartDisplayMedia({
        pin: displayPin,
        assetId: uploaded?.assetId ? Number(uploaded.assetId) : null,
        mediaUrl,
        mediaType,
        mimeType: mime,
        title: String(uploaded?.title || file.name || ''),
        sourcePageUrl: mediaUrl,
      });
      setShowMediaBar(false);
    } catch {
      /* ignore */
    } finally {
      setIsUploading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const displayAppUrl = typeof import.meta.env.VITE_APP_PUBLIC_URL === 'string' && import.meta.env.VITE_APP_PUBLIC_URL
    ? import.meta.env.VITE_APP_PUBLIC_URL
    : (typeof window !== 'undefined' ? window.location.origin : '');

  if (!displayPin) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-md bg-slate-950 p-6">
        <div className="max-w-md text-center">
          <Monitor className="mx-auto mb-4 h-12 w-12 text-slate-600" />
          <p className="text-base font-medium text-slate-400">Kein Display verbunden</p>
          <p className="mt-4 text-sm text-slate-500 leading-relaxed">
            Auf der <strong className="text-slate-400">digitalen Tafel</strong> die Adresse{' '}
            {displayAppUrl ? (
              <span className="rounded bg-slate-800/80 px-1.5 py-0.5 font-mono text-sky-300 break-all">{displayAppUrl}</span>
            ) : (
              <span className="font-mono text-sky-300">[App-URL]</span>
            )}
            {' '}öffnen, auf <strong className="text-slate-400">„Display öffnen“</strong> klicken, dann hier die <strong className="text-slate-400">6-stellige PIN</strong> eingeben (Monitor-Button oben).
          </p>
        </div>
      </div>
    );
  }

  if (isLoading && !status) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-md bg-slate-950">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative flex items-center justify-center overflow-hidden rounded-md bg-slate-950"
      style={isFullscreen && !document.fullscreenElement && !(document as any).webkitFullscreenElement
        ? { position: 'fixed', inset: 0, zIndex: 9999, width: '100vw', height: '100vh', borderRadius: 0 }
        : { width: '100%', height: '100%' }
      }
    >
      {/* Status badge – when waiting, pin can be minimized here; button to show big pin again */}
      <div className="absolute left-2 top-2 z-[70] flex items-center gap-1.5 rounded border border-slate-700 bg-slate-900/80 px-2 py-1">
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${status?.status === 'connected' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
        <span className="font-mono text-[11px] text-slate-400">{displayPin}</span>
        {status?.status !== 'connected' && <span className="text-[11px] text-amber-400">Warte auf Verbindung…</span>}
        {status?.status === 'waiting' && waitingPinInStatusBar && (
          <button
            type="button"
            onClick={() => setWaitingPinInStatusBar(false)}
            className="inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-700 hover:text-slate-200"
            title="Pin groß anzeigen"
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Fullscreen toggle */}
      <button
        type="button"
        onClick={handleToggleFullscreen}
        className="absolute right-2 top-2 z-[70] inline-flex h-7 w-7 items-center justify-center rounded border border-slate-600 bg-slate-900/80 text-slate-400 hover:border-slate-400 hover:text-slate-200"
        title={isFullscreen ? 'Vollbild verlassen' : 'Vollbild'}
      >
        {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
      </button>

      {/* Media upload / URL bar */}
      {status?.status === 'connected' && (
        <div className="absolute right-2 top-11 z-[70] flex flex-col items-end gap-1.5">
          <button
            type="button"
            onClick={() => setShowMediaBar((v) => !v)}
            className={`inline-flex h-7 items-center gap-1 rounded border px-2 text-[11px] ${showMediaBar ? 'border-sky-400 bg-sky-700/40 text-sky-200' : 'border-slate-600 bg-slate-900/80 text-slate-400 hover:border-slate-400 hover:text-slate-200'}`}
            title="Bild / Video / URL ans Display senden"
          >
            <ImagePlus className="h-3.5 w-3.5" />
          </button>
          {showMediaBar && (
            <div className="flex flex-col gap-1.5 rounded-lg border border-slate-600 bg-slate-900/95 p-2 shadow-xl">
              {/* File upload */}
              <button
                type="button"
                onClick={() => mediaFileInputRef.current?.click()}
                disabled={isUploading}
                className="inline-flex h-7 items-center gap-1.5 rounded border border-slate-600 bg-slate-800 px-2 text-[11px] text-slate-200 hover:bg-slate-700 disabled:opacity-50"
              >
                {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                {isUploading ? 'Lädt hoch…' : 'Datei hochladen'}
              </button>
              {/* URL input */}
              <div className="flex gap-1">
                <input
                  type="text"
                  value={mediaUrlInput}
                  onChange={(e) => setMediaUrlInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void handlePresentUrl(); }}
                  placeholder="YouTube- oder Bild-URL"
                  className="h-7 w-44 rounded border border-slate-600 bg-slate-800 px-2 text-[11px] text-slate-200 placeholder-slate-500 focus:border-sky-400 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => void handlePresentUrl()}
                  disabled={!mediaUrlInput.trim()}
                  className="inline-flex h-7 items-center rounded border border-slate-600 bg-slate-800 px-2 text-[11px] text-slate-200 hover:bg-slate-700 disabled:opacity-40"
                >
                  ➜
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={mediaFileInputRef}
        type="file"
        accept="image/*,video/*,application/pdf"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) await handleUploadFile(file);
          e.currentTarget.value = '';
        }}
      />

      {(status?.status === 'connected' || (status?.status === 'waiting' && waitingPinInStatusBar)) && (
        /* 1920×1080 logical canvas, scaled to fit the mirror container.
           When waiting + pin in status bar: whiteboard is shown so user can draw. */
        <div
          ref={canvasContainerRef}
          className="relative"
          style={{
            position: 'absolute',
            left: `${(containerSize.w - CANVAS_W * scale) / 2}px`,
            top: `${(containerSize.h - CANVAS_H * scale) / 2}px`,
            width: `${CANVAS_W}px`,
            height: `${CANVAS_H}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          {/* Text-Rechteck aufziehen und danach direkt tippen */}
          {textBoxDrawMode && (
            <div
              className="absolute inset-0 z-[56] cursor-crosshair bg-slate-900/35"
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                const p = pointerToCanvas(e);
                textBoxStartRef.current = { x: p.x, y: p.y, pointerId: e.pointerId };
                setTextBoxDraft({ x: p.x, y: p.y, width: 0, height: 0 });
                e.currentTarget.setPointerCapture(e.pointerId);
              }}
              onPointerMove={(e) => {
                const start = textBoxStartRef.current;
                if (!start || start.pointerId !== e.pointerId) return;
                const p = pointerToCanvas(e);
                setTextBoxDraft({
                  x: Math.min(start.x, p.x),
                  y: Math.min(start.y, p.y),
                  width: Math.abs(p.x - start.x),
                  height: Math.abs(p.y - start.y),
                });
              }}
              onPointerUp={(e) => {
                const start = textBoxStartRef.current;
                if (!start || start.pointerId !== e.pointerId) return;
                const p = pointerToCanvas(e);
                const draft = {
                  x: Math.min(start.x, p.x),
                  y: Math.min(start.y, p.y),
                  width: Math.abs(p.x - start.x),
                  height: Math.abs(p.y - start.y),
                };
                textBoxStartRef.current = null;
                setTextBoxDraft(null);
                setTextBoxDrawMode(false);
                if (draft.width >= 40 && draft.height >= 30) void createTextItemFromDraft(draft);
                try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
              }}
              onPointerCancel={(e) => {
                textBoxStartRef.current = null;
                setTextBoxDraft(null);
                setTextBoxDrawMode(false);
                try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  textBoxStartRef.current = null;
                  setTextBoxDraft(null);
                  setTextBoxDrawMode(false);
                }
              }}
              role="button"
              tabIndex={0}
              title="Rechteck aufziehen, um ein Textfeld zu erstellen"
            >
              {textBoxDraft && (
                <div
                  className="absolute border-2 border-sky-400 bg-sky-500/10"
                  style={{
                    left: `${textBoxDraft.x}px`,
                    top: `${textBoxDraft.y}px`,
                    width: `${textBoxDraft.width}px`,
                    height: `${textBoxDraft.height}px`,
                  }}
                />
              )}
              <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded bg-slate-800/90 px-3 py-1 text-xs text-slate-200">
                Rechteck aufziehen, dann direkt tippen
              </div>
            </div>
          )}
          {/* Drawing canvas – 1920×1080 logical resolution */}
          <canvas
            ref={drawingCanvasRef}
            className={`absolute inset-0 ${activeDrawTool ? 'z-[55] cursor-crosshair pointer-events-auto' : 'z-[5] pointer-events-none'}`}
            style={{ width: `${CANVAS_W}px`, height: `${CANVAS_H}px` }}
            onPointerDown={beginDrawing}
            onPointerMove={moveDrawing}
            onPointerUp={endDrawing}
            onPointerCancel={endDrawing}
          />
          {zirkel.visible && (
            <canvas
              ref={zirkelPreviewCanvasRef}
              className="absolute inset-0 z-[55] pointer-events-none"
              style={{ width: `${CANVAS_W}px`, height: `${CANVAS_H}px` }}
            />
          )}

          {geodreieck.visible && (
            <div className="absolute inset-0 z-[180] pointer-events-none">
              <GeodreieckOverlay
                x={geodreieck.x}
                y={geodreieck.y}
                rotation={geodreieck.rotation}
                scale={geodreieck.scale}
                displayScale={scale}
                interactive
                onMove={(x, y) => {
                  overlayLocalUpdateAtRef.current = Date.now();
                  if (showMathGrid) {
                    const s = snapToGrid(x, y);
                    x = s.x;
                    y = s.y;
                  }
                  setGeodreieck((g) => ({ ...g, x, y }));
                }}
                onRotate={(rotation) => {
                  overlayLocalUpdateAtRef.current = Date.now();
                  setGeodreieck((g) => ({ ...g, rotation }));
                }}
                onScale={(scale) => {
                  overlayLocalUpdateAtRef.current = Date.now();
                  setGeodreieck((g) => ({ ...g, scale }));
                }}
                snapToGrid={showMathGrid ? snapToGrid : undefined}
              />
            </div>
          )}
          {calculator.visible && (
            <div className="absolute inset-0 z-[180] pointer-events-none">
              <CalculatorOverlay
                x={calculator.x}
                y={calculator.y}
                displayScale={scale}
                interactive
                onMove={(x, y) => {
                  overlayLocalUpdateAtRef.current = Date.now();
                  setCalculator((c) => ({ ...c, x, y }));
                }}
              />
            </div>
          )}
          {zirkel.visible && (
            <div className="absolute inset-0 z-[180] pointer-events-none">
              <ZirkelOverlay
                x={zirkel.x}
                y={zirkel.y}
                radius={zirkel.radius}
                legLength={zirkel.legLength ?? DEFAULT_ZIRKEL.legLength}
                displayScale={scale}
                interactive
                onMove={(x, y) => {
                  overlayLocalUpdateAtRef.current = Date.now();
                  if (showMathGrid) {
                    const s = snapToGrid(x, y);
                    x = s.x;
                    y = s.y;
                  }
                  setZirkel((z) => ({ ...z, x, y }));
                }}
                onRadiusChange={(radius) => {
                  overlayLocalUpdateAtRef.current = Date.now();
                  setZirkel((z) => ({ ...z, radius }));
                }}
                onDrawArc={handleZirkelDrawArc}
                onArcPreview={setZirkelArcPreview}
                snapToGrid={showMathGrid ? snapToGrid : undefined}
              />
            </div>
          )}

          {/* Media items – positions in Display coords */}
          {visibleItems.length > 0 ? (
            <div className="absolute inset-0 z-20">
              {[...visibleItems]
                .sort((a, b) => (mediaLayout[a.id]?.z || 0) - (mediaLayout[b.id]?.z || 0))
                .map((item, index) => {
                  const layout = mediaLayout[item.id];
                  if (!layout) return null;
                  const textBodyPx = Math.max(18, Math.min(72, Math.round(layout.widthPx * 0.06)));
                  const textTitlePx = Math.max(11, Math.min(26, Math.round(layout.widthPx * 0.024)));
                  const handlesVisible = activeHandlesId === item.id;
                  const handleClass = `transition-opacity duration-500 ${handlesVisible ? (handlesFading ? 'opacity-0' : 'opacity-100') : 'opacity-0 pointer-events-none'}`;
                  return (
                    <div key={item.id} data-media-item-shell="true" className="absolute left-1/2 top-1/2"
                      style={{ zIndex: 90 + (layout.z || index), transform: `translate(calc(-50% + ${layout.x}px), calc(-50% + ${layout.y}px)) rotate(${layout.rotationDeg || 0}deg)` }}>
                      <div
                        ref={(el) => {
                          if (!el) return;
                          itemSizeRef.current[item.id] = { width: el.offsetWidth, height: el.offsetHeight };
                        }}
                        className="relative inline-block touch-none select-none rounded-2xl border border-emerald-600/40 bg-slate-900 p-2 shadow-2xl"
                        onPointerDown={() => { activateHandles(item.id); }}>
                        <button
                          type="button"
                          onPointerDown={(e) => { activateHandles(item.id); handleStartMediaDrag(item.id, 'move', e); }}
                          className={`absolute left-2 right-2 -top-5 z-30 inline-flex h-6 items-center justify-center rounded border border-slate-500 bg-slate-900/95 text-[10px] text-slate-200 hover:bg-slate-800 ${handleClass}`}
                          title="Verschieben"
                        >
                          ↔ Verschieben
                        </button>
                        <div className={`absolute -right-3 -top-3 z-30 inline-flex items-center gap-1 ${handleClass}`} data-no-drag>
                          <button type="button" onClick={() => { activateHandles(item.id); moveLayer(item.id, 'up'); }}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-xs text-slate-100 hover:bg-slate-800">↑</button>
                          <button type="button" onClick={() => { activateHandles(item.id); moveLayer(item.id, 'down'); }}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-xs text-slate-100 hover:bg-slate-800">↓</button>
                          <button type="button" onClick={() => { activateHandles(item.id); setMediaLayout((p) => { const n = { ...p, [item.id]: { ...(p[item.id] || { x: 0, y: 0, z: 1, rotationDeg: 0, visible: true }), widthPx: 260 } }; mediaLayoutRef.current = n; return n; }); scheduleLayoutSync(); }}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-xs text-slate-100 hover:bg-slate-800">–</button>
                          <button type="button" onClick={() => { activateHandles(item.id); const w = Math.round(CANVAS_W * 0.85); setMediaLayout((p) => { const n = { ...p, [item.id]: { ...(p[item.id] || { x: 0, y: 0, z: 1, rotationDeg: 0, visible: true }), widthPx: w } }; mediaLayoutRef.current = n; return n; }); scheduleLayoutSync(); }}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-xs text-slate-100 hover:bg-slate-800">⤢</button>
                          <button type="button" data-no-drag onClick={() => handleDismiss(item.id)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-xs text-slate-100 hover:bg-red-900 hover:border-red-500">✕</button>
                        </div>
                        <button type="button" data-no-drag
                          onPointerDown={(e) => { activateHandles(item.id); handleStartMediaDrag(item.id, 'resize', e); }}
                          className={`absolute -bottom-3 -right-3 z-30 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-xs text-slate-100 hover:bg-slate-800 ${handleClass}`}
                          title="Größe + Drehen">◢</button>
                        <div className="flex items-center justify-center" data-no-drag data-media-item-content="true">
                          {item.type === 'text' ? (
                            <div style={{ width: `${layout.widthPx}px` }} className="outline-none">
                              <div
                                ref={(el) => {
                                  if (el) {
                                    if (!textItemRefs.current[item.id]) textItemRefs.current[item.id] = { titleEl: null, bodyEl: null };
                                    textItemRefs.current[item.id].titleEl = el;
                                  }
                                }}
                                contentEditable
                                suppressContentEditableWarning
                                className="mb-3 font-semibold uppercase tracking-widest text-emerald-400 outline-none empty:before:content-['Titel'] empty:before:text-slate-500"
                                style={{ fontSize: `${textTitlePx}px`, minHeight: `${textTitlePx}px` }}
                                onFocus={() => setEditingTextItemId(item.id)}
                                onInput={(e) => {
                                  const title = e.currentTarget.innerText.replace(/\s+/g, ' ').trim();
                                  scheduleTextItemUpdate(item.id, { title: title || undefined });
                                }}
                                onBlur={(e) => {
                                  const titleEl = e.currentTarget;
                                  const title = titleEl.innerText.replace(/\s+/g, ' ').trim();
                                  setEditingTextItemId(null);
                                  flushTextItemUpdate(item.id, { title: title || undefined });
                                }}
                              />
                              <div
                                ref={(el) => {
                                  if (el) {
                                    if (!textItemRefs.current[item.id]) textItemRefs.current[item.id] = { titleEl: null, bodyEl: null };
                                    textItemRefs.current[item.id].bodyEl = el;
                                  }
                                }}
                                contentEditable
                                suppressContentEditableWarning
                                className="whitespace-pre-wrap font-medium leading-relaxed text-white outline-none empty:before:content-['Text_eingeben…'] empty:before:text-slate-500"
                                style={{ fontSize: `${textBodyPx}px` }}
                                onFocus={() => setEditingTextItemId(item.id)}
                                onInput={(e) => {
                                  scheduleTextItemUpdate(item.id, { text: e.currentTarget.innerText });
                                }}
                                onBlur={(e) => {
                                  const bodyEl = e.currentTarget;
                                  const text = bodyEl.innerText;
                                  setEditingTextItemId(null);
                                  flushTextItemUpdate(item.id, { text });
                                }}
                              />
                            </div>
                          ) : item.type === 'media' ? (
                            (item as any).mediaType === 'video' ? (
                              <video src={(item as any).mediaUrl} controls autoPlay style={{ width: `${layout.widthPx}px` }} className="h-auto rounded-xl object-contain" />
                            ) : (item as any).mediaType === 'pdf' ? (
                              <iframe src={buildPdfDisplayUrl((item as any).mediaUrl)} title={item.title || 'PDF'} style={{ width: `${Math.max(layout.widthPx, 220)}px`, height: `${Math.round(Math.max(layout.widthPx, 220) * 1.35)}px`, pointerEvents: isDragging ? 'none' : 'auto' }} className="rounded-xl border border-slate-700 bg-white" />
                            ) : (item as any).mediaType === 'youtube' ? (
                              <iframe src={buildYouTubeEmbedUrl((item as any).mediaUrl)} title={item.title || 'YouTube'} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ width: `${Math.max(layout.widthPx, 320)}px`, height: `${Math.round(Math.max(layout.widthPx, 320) * 0.5625)}px`, pointerEvents: isDragging ? 'none' : 'auto' }} className="rounded-xl border border-slate-700 bg-black" />
                            ) : (
                              <img src={(item as any).mediaUrl} alt={item.title || 'Medium'} draggable={false} style={{ width: `${layout.widthPx}px` }} className="h-auto rounded-xl object-contain" />
                            )
                          ) : (
                            <img src={(item as any).imageUrl} alt={item.title || 'Bild'} draggable={false} style={{ width: `${layout.widthPx}px` }} className="h-auto rounded-xl object-contain" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : status?.status === 'connected' ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <div className="rounded-2xl border border-emerald-600/40 bg-slate-900 px-8 py-6 shadow-2xl text-center">
                <p className="text-2xl font-bold text-white">{status?.connectedContext?.theme || 'Display verbunden'}</p>
                <p className="mt-2 text-sm text-slate-400">Noch keine Inhalte auf der Tafel</p>
              </div>
            </div>
          ) : (
            /* Waiting + pin in status bar: empty whiteboard, ready to draw */
            null
          )}
        </div>
      )}

      {status?.status === 'waiting' && !waitingPinInStatusBar && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4">
          <div className="text-center">
            <p className="font-mono text-4xl font-bold tracking-widest text-sky-300">{displayPin}</p>
            <p className="mt-2 text-sm text-slate-400">Display wartet auf Verbindung</p>
          </div>
          <button
            type="button"
            onClick={() => setWaitingPinInStatusBar(true)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 text-sm text-slate-200 hover:bg-slate-700"
            title="Pin in die Statusleiste verschieben und Whiteboard freigeben"
          >
            <ChevronUp className="h-4 w-4" />
            Pin in Statusleiste
          </button>
        </div>
      )}

      {/* Einheitliche Leiste: Seitennavigation + Dateien/YouTube/Text + Zeichenwerkzeuge */}
      {(status?.status === 'connected' || (status?.status === 'waiting' && waitingPinInStatusBar)) && (
        <div className="absolute bottom-2 left-2 right-2 z-[70] flex flex-nowrap items-center justify-start gap-1.5 overflow-x-auto rounded-xl border border-slate-600 bg-slate-900/95 px-2 py-1.5">
          <button
            type="button"
            onClick={() => handleSetPage(activePageIndex - 1)}
            disabled={activePageIndex <= 0 || isPageSwitching}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border border-slate-500 bg-slate-800 text-sm text-slate-200 disabled:opacity-30 hover:bg-slate-700"
            title="Vorherige Seite"
          >‹</button>
          <span className="min-w-[3.5rem] shrink-0 text-center text-[11px] text-slate-300">
            {activePageIndex + 1} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => handleSetPage(activePageIndex + 1)}
            disabled={activePageIndex >= totalPages - 1 || isPageSwitching}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border border-slate-500 bg-slate-800 text-sm text-slate-200 disabled:opacity-30 hover:bg-slate-700"
            title="Nächste Seite"
          >›</button>
          <button
            type="button"
            onClick={handleAddPage}
            disabled={isPageSwitching}
            className="inline-flex h-7 shrink-0 items-center gap-1 rounded border border-slate-500 bg-slate-800 px-1.5 text-[11px] text-slate-200 disabled:opacity-30 hover:bg-slate-700"
            title="Neue Seite"
          >+ Seite</button>
          <button
            type="button"
            onClick={openDateienModal}
            className="inline-flex h-7 shrink-0 items-center gap-1 rounded border border-slate-500 bg-slate-800 px-1.5 text-[11px] text-slate-200 hover:bg-slate-700"
            title="Dateien – Bild/Video/PDF auf Display anzeigen"
          >
            <FolderOpen className="h-3.5 w-3.5" />
            <span>Dateien</span>
          </button>
          <button
            type="button"
            onClick={() => setShowMediaBar(true)}
            className="inline-flex h-7 shrink-0 items-center gap-1 rounded border border-slate-500 bg-slate-800 px-1.5 text-[11px] text-slate-200 hover:bg-slate-700"
            title="YouTube oder Bild-URL eingeben"
          >
            <Youtube className="h-3.5 w-3.5" />
            <span>YouTube</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setTextBoxDraft(null);
              textBoxStartRef.current = null;
              setTextBoxDrawMode((v) => !v);
              setActiveDrawTool(null);
            }}
            className={`inline-flex h-7 shrink-0 items-center gap-1 rounded border px-1.5 text-[11px] ${textBoxDrawMode ? 'border-sky-400 bg-sky-500/20 text-sky-200' : 'border-slate-500 bg-slate-800 text-slate-200 hover:bg-slate-700'}`}
            title={textBoxDrawMode ? 'Rechteck aufziehen (nochmal klicken: abbrechen)' : 'Textfeld aufziehen und direkt tippen'}
          >
            <FileText className="h-3.5 w-3.5" />
            <span>Text</span>
          </button>
          <div className="mx-0.5 h-5 w-px shrink-0 bg-slate-600" />
          <button type="button" onClick={() => setActiveDrawTool(null)}
            className={`inline-flex h-7 w-7 items-center justify-center rounded border text-xs ${isHandMode ? 'border-sky-400 bg-sky-500/20 text-sky-200' : 'border-slate-500 bg-slate-800 text-slate-200'}`} title="Hand-Modus">✋</button>
          {DRAW_TOOLS.map((tool) => {
            const title = tool === 'pen' ? 'Stift' : tool === 'marker' ? 'Marker' : tool === 'line' ? 'Linie' : tool === 'line-dashed' ? 'Gestrichelte Linie' : tool === 'line-ruler' ? 'Linie mit Skaleneinteilung' : 'Radierer';
            const label = tool === 'pen' ? '✎' : tool === 'marker' ? '🖍' : tool === 'line' ? '／' : tool === 'line-dashed' ? '− −' : tool === 'line-ruler' ? '▬▬' : '⌫';
            return (
              <button key={tool} type="button" onClick={() => setActiveDrawTool(tool)}
                className={`inline-flex h-7 w-7 items-center justify-center rounded border text-xs ${activeDrawTool === tool ? 'border-sky-400 bg-sky-500/20 text-sky-200' : 'border-slate-500 bg-slate-800 text-slate-200'}`} title={title}>
                {label}
              </button>
            );
          })}
          <div className="relative inline-block">
            <button
              ref={shapeButtonRef}
              type="button"
              onClick={() => setShowShapeMenu((v) => !v)}
              className={`inline-flex h-7 w-7 items-center justify-center rounded border text-xs ${
                activeDrawTool && (SHAPE_TOOLS as readonly string[]).includes(activeDrawTool) ? 'border-sky-400 bg-sky-500/20 text-sky-200' : 'border-slate-500 bg-slate-800 text-slate-200'
              }`}
              title="Grundformen"
            >
              <Shapes className="h-3.5 w-3.5" />
            </button>
            {showShapeMenu && shapeMenuPosition && typeof document !== 'undefined' && createPortal(
              <div
                className="fixed z-[200] rounded-lg border border-slate-600 bg-slate-800 py-1 shadow-xl"
                style={{ left: shapeMenuPosition.left, bottom: shapeMenuPosition.bottom }}
              >
                {(['rectangle', 'square', 'circle', 'oval', 'triangle'] as const).map((shape) => (
                  <button
                    key={shape}
                    type="button"
                    onClick={() => { setActiveDrawTool(shape); setShowShapeMenu(false); }}
                    className="block w-full px-2.5 py-1 text-left text-[11px] text-slate-200 hover:bg-slate-700"
                  >
                    {shape === 'rectangle' ? 'Rechteck' : shape === 'square' ? 'Quadrat' : shape === 'circle' ? 'Kreis' : shape === 'oval' ? 'Oval' : 'Dreieck'}
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>
          <div className="mx-0.5 h-5 w-px bg-slate-600" />
          {DRAW_COLORS.map((color) => (
            <button key={color} type="button" onClick={() => setActiveDrawColor(color)}
              className={`h-6 w-6 rounded-full border-2 ${activeDrawColor === color ? 'border-white' : 'border-slate-500'}`} style={{ backgroundColor: color }} />
          ))}
          <div className="mx-0.5 h-5 w-px bg-slate-600" />
          <input type="range" min={1} max={24} value={drawWidth} onChange={(e) => setDrawWidth(Number(e.target.value) || 4)} className="w-16 accent-sky-400" />
          <button type="button" onClick={handleUndo} disabled={localDrawStrokes.length === 0}
            className="inline-flex h-7 items-center rounded border border-slate-500 bg-slate-800 px-2 text-[11px] text-slate-200 disabled:opacity-40">↩</button>
          <button type="button" onClick={handleRedo} disabled={redoStrokes.length === 0}
            className="inline-flex h-7 items-center rounded border border-slate-500 bg-slate-800 px-2 text-[11px] text-slate-200 disabled:opacity-40">↪</button>
          <button type="button" onClick={handleClearDrawing} disabled={localDrawStrokes.length === 0}
            className="inline-flex h-7 items-center rounded border border-red-500/70 bg-red-700/30 px-2 text-[11px] text-red-100 disabled:opacity-40">✕</button>
          <div className="relative inline-block">
            <button
              type="button"
              onClick={() => {
                overlayLocalUpdateAtRef.current = Date.now();
                const willShow = !geodreieck.visible;
                setGeodreieck((g) => ({ ...g, visible: !g.visible }));
                if (willShow) setActiveDrawTool(null);
              }}
              className={`inline-flex h-7 w-7 items-center justify-center rounded border text-slate-200 hover:bg-slate-700 ${geodreieck.visible ? 'border-sky-400 bg-sky-500/20' : 'border-slate-500 bg-slate-800'}`}
              title="Geodreieck"
            >
              <Triangle className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => {
                overlayLocalUpdateAtRef.current = Date.now();
                const willShow = !calculator.visible;
                setCalculator((c) => ({ ...c, visible: !c.visible }));
                if (willShow) setActiveDrawTool(null);
              }}
              className={`inline-flex h-7 w-7 items-center justify-center rounded border text-slate-200 hover:bg-slate-700 ${calculator.visible ? 'border-sky-400 bg-sky-500/20' : 'border-slate-500 bg-slate-800'}`}
              title="Taschenrechner"
            >
              <Calculator className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => {
                overlayLocalUpdateAtRef.current = Date.now();
                const willShow = !zirkel.visible;
                setZirkel((z) => ({ ...z, visible: !z.visible }));
                if (willShow) setActiveDrawTool(null);
              }}
              className={`inline-flex h-7 w-7 items-center justify-center rounded border text-slate-200 hover:bg-slate-700 ${zirkel.visible ? 'border-sky-400 bg-sky-500/20' : 'border-slate-500 bg-slate-800'}`}
              title="Zirkel"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="8" r="2" /><path d="M12 10 8 22M12 10l4 12" /></svg>
            </button>
            {zirkel.visible && (
              <label className="inline-flex items-center gap-1 text-slate-300" title="Schenkellänge">
                <span className="text-[9px] whitespace-nowrap">Schenkel</span>
                <input
                  type="range"
                  min={LEG_LENGTH_MIN}
                  max={LEG_LENGTH_MAX}
                  value={Math.max(LEG_LENGTH_MIN, Math.min(LEG_LENGTH_MAX, zirkel.legLength ?? DEFAULT_ZIRKEL.legLength))}
                  onChange={(e) => {
                    overlayLocalUpdateAtRef.current = Date.now();
                    setZirkel((z) => ({ ...z, legLength: Number(e.target.value) }));
                  }}
                  className="w-12 h-5 accent-sky-400"
                />
              </label>
            )}
            <button
              ref={backgroundButtonRef}
              type="button"
              onClick={() => setShowBackgroundMenu((v) => !v)}
              className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-500 bg-slate-800 text-slate-200 hover:bg-slate-700"
              title="Hintergrund & Linien"
            >
              <Palette className="h-3.5 w-3.5" />
            </button>
            {showBackgroundMenu && backgroundMenuPosition && typeof document !== 'undefined' && createPortal(
              <div
                className="fixed z-[200] w-44 rounded-lg border border-slate-600 bg-slate-800 p-2 shadow-xl"
                style={{ left: backgroundMenuPosition.left, bottom: backgroundMenuPosition.bottom }}
              >
                <p className="mb-1 px-1 text-[11px] font-medium text-slate-400">Hintergrund</p>
                <div className="flex flex-wrap gap-1">
                  {BACKGROUND_PRESETS.map(({ label, value }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setBackgroundColor(value)}
                      className={`h-5 rounded border px-1.5 text-[10px] ${backgroundColor === value ? 'border-sky-400 ring-1 ring-sky-400' : 'border-slate-600'}`}
                      style={{ backgroundColor: value, color: value === '#ffffff' || value === '#f1f5f9' ? '#1e293b' : '#e2e8f0' }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 mb-0.5 px-1 text-[11px] font-medium text-slate-400">Hilfslinien</p>
                <label className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-[11px] text-slate-200 hover:bg-slate-700">
                  <input type="checkbox" checked={showWritingLines} onChange={(e) => setShowWritingLines(e.target.checked)} className="rounded accent-sky-400" />
                  Schreiblinien
                </label>
                <label className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-[11px] text-slate-200 hover:bg-slate-700">
                  <input type="checkbox" checked={showMathGrid} onChange={(e) => setShowMathGrid(e.target.checked)} className="rounded accent-sky-400" />
                  Rechenkaro
                </label>
                <p className="mt-1.5 mb-0.5 px-1 text-[11px] font-medium text-slate-400">Geodreieck</p>
                <label className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-[11px] text-slate-200 hover:bg-slate-700">
                  <input
                    type="checkbox"
                    checked={geodreieck.visible}
                    onChange={(e) => {
                      overlayLocalUpdateAtRef.current = Date.now();
                      const checked = e.target.checked;
                      setGeodreieck((g) => ({ ...g, visible: checked }));
                      if (checked) setActiveDrawTool(null);
                    }}
                    className="rounded accent-sky-400"
                  />
                  Anzeigen
                </label>
                <p className="mt-1.5 mb-0.5 px-1 text-[11px] font-medium text-slate-400">Taschenrechner</p>
                <label className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-[11px] text-slate-200 hover:bg-slate-700">
                  <input
                    type="checkbox"
                    checked={calculator.visible}
                    onChange={(e) => {
                      overlayLocalUpdateAtRef.current = Date.now();
                      const checked = e.target.checked;
                      setCalculator((c) => ({ ...c, visible: checked }));
                      if (checked) setActiveDrawTool(null);
                    }}
                    className="rounded accent-sky-400"
                  />
                  Anzeigen
                </label>
                <p className="mt-1.5 mb-0.5 px-1 text-[11px] font-medium text-slate-400">Zirkel</p>
                <label className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-[11px] text-slate-200 hover:bg-slate-700">
                  <input
                    type="checkbox"
                    checked={zirkel.visible}
                    onChange={(e) => {
                      overlayLocalUpdateAtRef.current = Date.now();
                      const checked = e.target.checked;
                      setZirkel((z) => ({ ...z, visible: checked }));
                      if (checked) setActiveDrawTool(null);
                    }}
                    className="rounded accent-sky-400"
                  />
                  Anzeigen
                </label>
              </div>,
              document.body
            )}
          </div>
        </div>
      )}

      {/* Modal: Dateien – User-Dateien für Display auswählen */}
      {showDateienModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4" onClick={() => setShowDateienModal(false)}>
          <div className="max-h-[80vh] w-full max-w-lg rounded-xl border border-slate-600 bg-slate-800 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-600 px-4 py-3">
              <div className="flex items-center gap-2">
                {dateienFolderStack.length > 0 && (
                  <button type="button" onClick={navigateDateienBack} className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-200" title="Zurück">‹</button>
                )}
                <h2 className="text-sm font-semibold text-slate-200">Dateien – auf Display anzeigen</h2>
              </div>
              <button type="button" onClick={() => setShowDateienModal(false)} className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-200" title="Schließen">✕</button>
            </div>
            <p className="border-b border-slate-600 px-4 py-1.5 text-xs text-slate-400">Nur Bilder, Video und PDF. Klicke auf eine Datei, um sie auf dem Display zu zeigen.</p>
            <div className="overflow-y-auto p-4" style={{ maxHeight: 'calc(80vh - 120px)' }}>
              {dateienError && <p className="mb-3 rounded bg-red-900/40 px-3 py-2 text-sm text-red-200">{dateienError}</p>}
              {dateienLoading ? (
                <p className="py-8 text-center text-sm text-slate-400">Lade …</p>
              ) : (
                <div className="space-y-1">
                  {dateienContents.folders.map((folder) => (
                    <button
                      key={folder.id}
                      type="button"
                      onClick={() => navigateDateienFolder(folder)}
                      className="flex w-full items-center gap-2 rounded-lg border border-slate-600 bg-slate-700/50 px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-700"
                    >
                      <FolderOpen className="h-5 w-5 shrink-0 text-amber-400" />
                      {folder.name}
                    </button>
                  ))}
                  {dateienContents.files.filter((f) => isDisplaySupportedFile(f.mime_type)).map((file) => (
                    <button
                      key={file.file_id}
                      type="button"
                      onClick={() => presentDateienFile(file)}
                      className="flex w-full items-center gap-2 rounded-lg border border-slate-600 bg-slate-700/50 px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-600"
                    >
                      <span className="shrink-0 text-base">
                        {mimeToDisplayMediaType(file.mime_type) === 'image' ? '🖼' : mimeToDisplayMediaType(file.mime_type) === 'video' ? '🎬' : '📄'}
                      </span>
                      <span className="truncate">{file.original_name}</span>
                    </button>
                  ))}
                  {!dateienLoading && !dateienError && dateienContents.folders.length === 0 && dateienContents.files.filter((f) => isDisplaySupportedFile(f.mime_type)).length === 0 && (
                    <p className="py-4 text-center text-sm text-slate-500">Keine Ordner oder unterstützten Dateien (Bild, Video, PDF) in diesem Ordner.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
